import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { roadmapAreas, roadmapStatusGroups } from '../data/research';
import { easeOutStrong, fadeUp } from '../utils/motion';
export function RoadmapPage() {
  const scrollToArea = (id: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <>
      <section className="bg-white border-b border-line pt-32 pb-20 lg:pt-44 lg:pb-24" data-id="element-3407" data-mp="dklu6dw5elx9">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: easeOutStrong
      }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-3408" data-mp="ijggvqx8kumn">
          <div className="lg:col-span-5" data-id="element-3409" data-mp="2oc537y7xchft">
            <p className="text-sm font-medium text-teal mb-5" data-id="element-3410" data-mp="2t8viwkfthb0p">Roadmap</p>
            <h1 className="font-heading font-extrabold text-5xl lg:text-6xl text-ink-dark leading-[1.05] tracking-tight" data-id="element-3411" data-mp="2tb891kvqarou">
              Where SyNeural Is Going
            </h1>
            <p className="mt-5 text-lg text-ink-muted" data-id="element-3412" data-mp="2c3blc82fcwnv">
              Development roadmap and publication plans.
            </p>
          </div>
          <div className="lg:col-span-7 lg:pt-10" data-id="element-3413" data-mp="2346or7afbdec">
            <p className="text-2xl text-ink-dark leading-relaxed mb-6" data-id="element-3414" data-mp="235rjawwqmwc7">
              SyNeural studies how people develop sound judgment, how
              researchers connect ideas, and how institutions turn knowledge
              into responsible action.
            </p>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-3415" data-mp="2qsg69x0ltezo">
              This roadmap covers what is available now, what we are developing
              next across Noetivra and the Semantic Knowledge Platform (SKP),
              and the research behind CJI and NCJI. It is not a release
              schedule, and it does not claim that any proposed measure or
              application has been validated.
            </p>
          </div>
        </motion.div>
      </section>

      <section aria-labelledby="roadmap-status" className="bg-white py-20 lg:py-28" data-id="element-3416" data-mp="1gvri3vmn3yk1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3417" data-mp="12w05nzbruq85">
          <motion.div {...fadeUp} className="max-w-3xl mb-12" data-id="element-3418" data-mp="12xuhmo52f803">
            <h2 id="roadmap-status" className="font-heading font-bold text-3xl lg:text-4xl text-ink-dark tracking-tight leading-tight mb-5" data-id="element-3419" data-mp="17ti7z6vaa776">
              What is available and what comes next
            </h2>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-3420" data-mp="k58s0lkr4dny">
              These groups describe status, not a committed order or release
              schedule.
            </p>
          </motion.div>
          <motion.div {...fadeUp} className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-10" data-id="element-3421" data-mp="a72u0f22frod">
            {roadmapStatusGroups.map((group, index) => <section key={group.title} aria-labelledby={`status-${index}`} className="border-t-2 border-navy pt-6" data-id="element-3422" data-mp="f5mfyfgnt3gq" data-mpi={index}>
                <h3 id={`status-${index}`} className="font-heading font-bold text-xl text-ink-dark mb-4" data-id="element-3423" data-mp="3rrujltvjheci">
                  {group.title}
                </h3>
                <ul className="space-y-3" data-id="element-3424" data-mp="5jl2slzoyiy8">
                  {group.items.map(item => <li key={item} className="text-ink-muted leading-relaxed" data-id="element-3425" data-mp="ifbmsloj3qt">
                      {item}
                    </li>)}
                </ul>
                {index === 0 && <a href="/noetivra/demo-center" className="mt-6 inline-flex items-center gap-2 text-navy font-semibold hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-3426" data-mp="9jdh7fbrnjbi">
                    Explore the demos
                    <ArrowRight size={16} aria-hidden="true" data-id="element-3427" data-mp="xar8rj23favf" />
                  </a>}
              </section>)}
          </motion.div>
        </div>
      </section>

      <section className="bg-surface-bg py-20 lg:py-28" data-id="element-3428" data-mp="2mrmsjc6r5mm3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-3429" data-mp="2mp0m5fvlld5g">
          <nav aria-label="Roadmap directions" className="hidden lg:block lg:col-span-4" data-id="element-3430" data-mp="2wgwm6wh8vdcb">
            <div className="sticky top-28" data-id="element-3431" data-mp="31frecp13qe93">
              <p className="text-sm font-medium text-ink-muted mb-4" data-id="element-3432" data-mp="31eptxty74fep">
                {roadmapAreas.length} research directions
              </p>
              <ul className="border-l border-line" data-id="element-3433" data-mp="23ubws1kbxbbe">
                {roadmapAreas.map(area => <li key={area.id} data-id="element-3434" data-mp="28swv1vg0tkx7">
                    <a href={`#${area.id}`} onClick={scrollToArea(area.id)} className="block -ml-px border-l-2 border-transparent hover:border-navy pl-4 py-1.5 text-sm text-ink-muted hover:text-navy transition-colors duration-150" data-id="element-3435" data-mp="2dpx146cvenob">
                      {area.title}
                    </a>
                  </li>)}
              </ul>
            </div>
          </nav>

          <div className="lg:col-span-8" data-id="element-3436" data-mp="1dhymq306tzi8">
            {roadmapAreas.map(area => <motion.article key={area.id} id={area.id} {...fadeUp} className="scroll-mt-28 border-t border-line py-12 first:border-t-0 first:pt-0" data-id="element-3437" data-mp="1dkcjqhefhlsf">
                <h2 className="font-heading font-bold text-3xl text-ink-dark tracking-tight mb-5" data-id="element-3438" data-mp="ytlrj88xckos">
                  {area.title}
                </h2>
                <p className="text-xl text-ink-dark leading-relaxed mb-5" data-id="element-3439" data-mp="tsoa4xvqnuoc">{area.lead}</p>
                <div className="space-y-4" data-id="element-3440" data-mp="psxlazprvomg">
                  {area.paragraphs.map(paragraph => <p key={paragraph} className="text-lg text-ink-muted leading-relaxed" data-id="element-3441" data-mp="kv5kfg75gs64">
                      {paragraph}
                    </p>)}
                </div>
              </motion.article>)}
          </div>
        </div>
      </section>

      <section className="bg-navy text-white py-20 lg:py-28" data-id="element-3442" data-mp="2yb5m44ddvkoj">
        <motion.div {...fadeUp} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3443" data-mp="332fbd3v0slzw">
          <h2 className="font-heading font-bold text-4xl lg:text-5xl tracking-tight mb-6" data-id="element-3444" data-mp="2pbiuubedbv97">
            From roadmap to responsible application
          </h2>
          <p className="text-xl text-white/80 leading-relaxed mb-10" data-id="element-3445" data-mp="2ualq544jqn3z">
            SyNeural’s work connects three activities: organizing knowledge,
            developing human capability, and improving institutional decisions.
            SKP helps connect and govern what is known. CJI and NCJI explore how
            people develop judgment. Noetivra’s interactive demos show how both
            could be used in practice. Pilots and independent review test whether
            the resulting tools work as intended.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-3446" data-mp="21lokq1lfoksn">
            <a href="/research" className="inline-flex items-center justify-center gap-2 bg-white text-navy hover:bg-surface-muted font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-3447" data-mp="21jlimj70b17k">
              Explore the Research Agenda
              <ArrowRight size={18} data-id="element-3448" data-mp="230r70osnia2w" />
            </a>
            <a href="/partnerships" className="inline-flex items-center justify-center border border-white/40 text-white hover:bg-white/10 font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-3449" data-mp="1y4tteq8rvhtt">
              Discuss a Research Partnership
            </a>
          </div>
        </motion.div>
      </section>
    </>;
}